import {
  Ban,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  Eye,
  EyeOff,
  Tags,
} from "lucide-react";
import { Button } from "../../../components/button";
import { useState } from "react";
import { DataTable, type DataTableColumn } from "../../../components/table/data-table";
import type { MerchantBrandItem } from "../types/merchantBrand.types";
import type { MerchantBrandCatalogListItem } from "../types/merchantBrandAdmin.types";

interface MerchantBrandsTableProps {
  brands: MerchantBrandCatalogListItem[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onUpdateCategories: (brandId: string) => void;
  onToggleActive: (brand: MerchantBrandCatalogListItem) => void;
}

export function MerchantBrandsTable({
  brands,
  loading,
  error,
  onRetry,
  onUpdateCategories,
  onToggleActive,
}: MerchantBrandsTableProps) {
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);

  const brandColumns: DataTableColumn<MerchantBrandCatalogListItem>[] = [
    {
      key: "expand",
      title: "",
      minWidth: 56,
      render: (brand) =>
        brand.id === selectedBrandId ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        ),
    },
    {
      key: "name",
      title: "Merchant brand",
      minWidth: 220,
      render: (brand) =>
        brand.match_status === "need_sync" && !brand.is_active ? (
          <span className="inline-flex items-center gap-2 font-semibold text-amber-950 dark:text-amber-100">
            <EyeOff
              className="h-4 w-4 text-amber-600"
              aria-hidden="true"
            />
            {brand.brand_name}
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/60 dark:text-amber-100">
              Hidden
            </span>
          </span>
        ) : (
          <span className="font-semibold">{brand.brand_name}</span>
        ),
    },
    {
      key: "brand_key",
      title: "Brand key",
      minWidth: 220,
      render: (brand) => (
        <span className="font-mono text-xs text-gray-500">{brand.brand_key || "—"}</span>
      ),
    },
    {
      key: "catalog",
      title: "Catalog",
      minWidth: 180,
      render: (brand) => brand.catalog_name || "—",
    },
    {
      key: "status",
      title: "Tango status",
      minWidth: 180,
      render: (brand) =>
        brand.match_status === "need_sync" ? (
          <span className="inline-flex items-center gap-2 text-emerald-700">
            <CircleCheck className="h-4 w-4" /> Needs sync
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 text-amber-700">
            <CircleAlert className="h-4 w-4" />
            {brand.match_status === "need_create" ? "Need create" : "Need delete"}
          </span>
        ),
    },
    {
      key: "actions",
      title: "Actions",
      align: "right",
      minWidth: 240,
      render: (brand) => (
        <div className="flex justify-end gap-2">
          {brand.match_status === "need_sync" ? (
            <Button
              variant="outline"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                onToggleActive(brand);
              }}
              aria-label={`${brand.is_active ? "Disable" : "Enable"} ${brand.brand_name}`}
            >
              {brand.is_active ? (
                <Ban className="mr-2 h-4 w-4 text-red-600" />
              ) : (
                <Eye className="mr-2 h-4 w-4 text-emerald-600" />
              )}
              {brand.is_active ? "Disable" : "Enable"}
            </Button>
          ) : null}
          {brand.match_status === "need_sync" ? (
            <Button
              variant="outline"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                onUpdateCategories(brand.id);
              }}
              aria-label={`Update categories for ${brand.brand_name}`}
            >
              <Tags className="mr-2 h-4 w-4" />
              Update categories
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const itemColumns: DataTableColumn<MerchantBrandItem>[] = [
    {
      key: "item",
      title: "Item",
      minWidth: 180,
      render: (item) => item.reward_name || item.utid || "—",
    },
    { key: "utid", title: "UTID", minWidth: 180, render: (item) => item.utid || "—" },
    {
      key: "face_value",
      title: "Face value",
      minWidth: 120,
      render: (item) =>
        item.face_value === null
          ? "—"
          : `${item.currency_code ?? ""} ${item.face_value.toLocaleString()}`.trim(),
    },
    {
      key: "reward_type",
      title: "Reward type",
      minWidth: 120,
      render: (item) => item.reward_type || item.value_type || "—",
    },
    {
      key: "status",
      title: "Match status",
      minWidth: 180,
      render: () => <span className="text-gray-500">Comparison unavailable</span>,
    },
  ];

  return (
    <DataTable
      columns={brandColumns}
      dataSource={brands}
      rowKey="id"
      loading={loading}
      error={error}
      onRetry={onRetry}
      emptyLabel="No merchant brands found."
      rowClassName={(brand) =>
        brand.match_status === "need_sync" && !brand.is_active
          ? "bg-amber-50/70 hover:bg-amber-100/70 dark:bg-amber-950/20 dark:hover:bg-amber-950/30"
          : ""
      }
      onRowClick={(brand) => {
        const shouldSelect = selectedBrandId !== brand.id;
        setSelectedBrandId(shouldSelect ? brand.id : null);
      }}
      expandedRowKey={selectedBrandId}
      expandedRowClassName="bg-gray-50/70 dark:bg-gray-800/50"
      renderExpandedRow={(brand) => (
        <div className="p-4">
          <h2 className="mb-4 text-base font-semibold text-[#1D1D1F]">{brand.brand_name} items</h2>
          <DataTable
            columns={itemColumns}
            dataSource={brand.merchant_brand_items}
            rowKey="id"
            emptyLabel="No merchant brand items found."
          />
        </div>
      )}
    />
  );
}
